import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Moon, Sun, Home, PenTool, FileText, Loader2, Copy, Check, CheckCircle, RotateCcw, AlertCircle, Activity, Info, Search, ChevronDown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useDarkMode } from '../../context/DarkModeContext';
import { UserAvatar } from '../ui/user-avatar';
import { NotificationMenu } from '../notifications/NotificationMenu';
import { employeeNamesService } from '../../services/employeeNames';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AI_MODELS, MODEL_CATEGORIES, getModelsByCategory } from '../../utils/aiModels';

interface FormData {
  textType: string;
  length: string;
  mainKeyword: string;
  secondaryKeywords: string[];
  title: string;
  model: string;
}

// Group models by category for better UI organization
const getGroupedModels = () => {
  const grouped: { [key: string]: typeof AI_MODELS } = {};
  
  // Add all categories
  Object.keys(MODEL_CATEGORIES).forEach(category => {
    const models = getModelsByCategory(category);
    if (models.length > 0) {
      grouped[category] = models;
    }
  });
  
  return grouped;
};

const GROUPED_MODELS = getGroupedModels();

const SmartWriterDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { userEmail, logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  
  const [displayName, setDisplayName] = useState<string>('');
  const [availableKeywords, setAvailableKeywords] = useState<string[]>([]);
  const [contentTypes, setContentTypes] = useState<string[]>([]);
  const [isLoadingKeywords, setIsLoadingKeywords] = useState(false);
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<{title: string, text: string} | null>(null);
  const [error, setError] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [isRevising, setIsRevising] = useState(false);
  const [revisionInstructions, setRevisionInstructions] = useState('');
  const [showRevisionForm, setShowRevisionForm] = useState(false);
  const [usedKeywords, setUsedKeywords] = useState<string[]>([]);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [selectedRestoreKeyword, setSelectedRestoreKeyword] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [modelStatus, setModelStatus] = useState<any>(null);
  const [showModelInfo, setShowModelInfo] = useState(false);
  const [isCheckingModelHealth, setIsCheckingModelHealth] = useState(false);
  const [keywordSearch, setKeywordSearch] = useState('');
  const [showKeywordDropdown, setShowKeywordDropdown] = useState(false);
  const [filteredKeywords, setFilteredKeywords] = useState<string[]>([]);
  
  const [formData, setFormData] = useState<FormData>({
    textType: '',
    length: '1200 words',
    mainKeyword: '',
    secondaryKeywords: [],
    title: '',
    model: 'openai/gpt-3.5-turbo' // Updated default to a more reliable model
  });

  // Load display name
  useEffect(() => {
    const loadDisplayName = async () => {
      if (userEmail) {
        const name = await employeeNamesService.getNameFromEmail(userEmail);
        if (name) {
          setDisplayName(name);
        }
      }
    };
    loadDisplayName();
  }, [userEmail]);

  // Load keywords on component mount
  useEffect(() => {
    const loadKeywords = async () => {
      setIsLoadingKeywords(true);
      try {
        // First test basic function connectivity
        console.log('Testing basic function connectivity...');
        const pingResponse = await fetch('/.netlify/functions/smart-writer-ping', {
          method: 'GET'
        });
        
        if (!pingResponse.ok) {
          setError('Netlify functions not deployed properly. Check deployment status in Netlify dashboard.');
          setIsLoadingKeywords(false);
          return;
        }

        const pingData = await pingResponse.json();
        console.log('Function connectivity test:', pingData);

        // Try to load keywords with timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
        
        const response = await fetch('/.netlify/functions/smart-writer-fetch-keywords', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mode: 'list' }),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const data = await response.json();
          if (data.keywords && data.keywords.length > 0) {
            setAvailableKeywords(data.keywords);
            setError(''); // Clear any previous errors
          } else {
            setError('No keywords found in Google Sheet. Check if there are entries in column G starting at row 1130.');
          }
        } else {
          let errorMessage = `Failed to load keywords (${response.status})`;
          try {
            const errorData = await response.json();
            errorMessage += `: ${errorData.error || response.statusText}`;
          } catch {
            errorMessage += `: ${response.statusText}`;
          }
          setError(errorMessage);
        }
      } catch (err) {
        console.error('Keyword loading error:', err);
        if (err instanceof Error && err.name === 'AbortError') {
          setError('Request timed out. Functions may be cold starting - try again in a moment.');
        } else if (err instanceof Error && err.message.includes('JSON')) {
          setError('Functions returning HTML instead of JSON. Check Netlify function deployment.');
        } else {
          setError('Error connecting to keyword service. Check console for details and verify Netlify deployment.');
        }
      } finally {
        setIsLoadingKeywords(false);
      }
    };
    
    loadKeywords();
    loadUsedKeywords();
    loadContentTypes();
    loadModelStatus();
  }, []);

  // Filter keywords based on search term
  useEffect(() => {
    if (!keywordSearch.trim()) {
      setFilteredKeywords(availableKeywords);
    } else {
      const filtered = availableKeywords.filter(keyword =>
        keyword.toLowerCase().includes(keywordSearch.toLowerCase())
      );
      setFilteredKeywords(filtered);
    }
  }, [keywordSearch, availableKeywords]);

  // Load model status information
  const loadModelStatus = async () => {
    try {
      const response = await fetch('/.netlify/functions/smart-writer-model-status');
      if (response.ok) {
        const data = await response.json();
        setModelStatus(data);
      }
    } catch (err) {
      console.error('Error loading model status:', err);
    }
  };

  // Check specific model health
  const checkModelHealth = async (modelId: string) => {
    setIsCheckingModelHealth(true);
    try {
      const response = await fetch(`/.netlify/functions/smart-writer-model-status?health=true&model=${encodeURIComponent(modelId)}`);
      if (response.ok) {
        const healthData = await response.json();
        console.log(`Model ${modelId} health:`, healthData);
        
        // Show health status in UI
        const healthMessage = healthData.healthy
          ? `✅ ${modelId} is healthy (${healthData.responseTime}ms)`
          : `❌ ${modelId} is unhealthy: ${healthData.error}`;
        
        // You could show this in a toast or alert
        alert(healthMessage);
      }
    } catch (err) {
      console.error('Error checking model health:', err);
      alert(`❌ Failed to check ${modelId} health`);
    } finally {
      setIsCheckingModelHealth(false);
    }
  };

  // Load content types from sheet
  const loadContentTypes = async () => {
    setIsLoadingTypes(true);
    try {
      const response = await fetch('/.netlify/functions/smart-writer-fetch-types');
      if (response.ok) {
        const data = await response.json();
        setContentTypes(data.types || []);
      } else {
        console.error('Failed to load content types');
      }
    } catch (err) {
      console.error('Error loading content types:', err);
    } finally {
      setIsLoadingTypes(false);
    }
  };

  // Load used keywords
  const loadUsedKeywords = async () => {
    try {
      const response = await fetch('/.netlify/functions/smart-writer-get-used-keywords');
      if (response.ok) {
        const data = await response.json();
        setUsedKeywords(data.keywords || []);
      }
    } catch (err) {
      console.error('Error loading used keywords:', err);
    }
  };

  // Load secondary keywords and title when main keyword changes
  useEffect(() => {
    const loadKeywordDetails = async () => {
      if (!formData.mainKeyword) {
        setFormData(prev => ({ ...prev, secondaryKeywords: [], title: '' }));
        return;
      }

      try {
        const response = await fetch('/.netlify/functions/smart-writer-fetch-keywords', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mode: 'byValue', value: formData.mainKeyword })
        });
        
        if (response.ok) {
          const data = await response.json();
          setFormData(prev => ({
            ...prev,
            secondaryKeywords: data.secondaryKeywords || [],
            title: data.title || ''
          }));
        }
      } catch (err) {
        console.error('Error loading keyword details:', err);
      }
    };
    
    if (formData.mainKeyword) {
      loadKeywordDetails();
    }
  }, [formData.mainKeyword]);

  const handleFormChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  // Handle keyword search input
  const handleKeywordSearch = (e: any) => {
    const value = e.target.value;
    setKeywordSearch(value);
    setShowKeywordDropdown(true);
    
    // If user clears the search, also clear the selected keyword
    if (!value.trim()) {
      handleFormChange('mainKeyword', '');
    }
  };

  // Handle keyword selection from dropdown
  const handleKeywordSelect = (keyword: string) => {
    handleFormChange('mainKeyword', keyword);
    setKeywordSearch(keyword);
    setShowKeywordDropdown(false);
  };

  // Handle clicking outside to close dropdown
  const handleKeywordInputBlur = () => {
    // Delay hiding to allow for click on dropdown items
    setTimeout(() => setShowKeywordDropdown(false), 150);
  };

  const handleGenerate = async () => {
    // Validation
    if (!formData.textType) {
      setError('Please select a content type');
      return;
    }
    if (!formData.mainKeyword) {
      setError('Please select a main keyword');
      return;
    }
    if (!formData.length) {
      setError('Please specify target length');
      return;
    }

    setIsGenerating(true);
    setError('');
    
    try {
      const response = await fetch('/.netlify/functions/smart-writer-generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          typeLabel: formData.textType,
          length: formData.length,
          mainKeyword: formData.mainKeyword,
          secondaryKeywords: formData.secondaryKeywords,
          title: formData.title,
          model: formData.model
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setGeneratedContent({
          title: data.finalTitle,
          text: data.fullText
        });
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to generate content');
      }
    } catch (err) {
      setError('Error generating content');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRevise = async () => {
    if (!generatedContent || !revisionInstructions.trim()) {
      return;
    }

    setIsRevising(true);
    setError('');
    
    try {
      const fullContent = `# ${generatedContent.title}\n\n${generatedContent.text}`;
      
      const response = await fetch('/.netlify/functions/smart-writer-revise', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          originalContent: fullContent,
          revisionInstructions: revisionInstructions,
          model: formData.model
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Extract title and content from the revised markdown
        const lines = data.revisedContent.split('\n');
        let newTitle = generatedContent.title;
        let newContent = data.revisedContent;
        
        // Check if the first line is a markdown title
        if (lines[0] && lines[0].startsWith('# ')) {
          newTitle = lines[0].substring(2).trim();
          newContent = lines.slice(1).join('\n').trim();
        }
        
        setGeneratedContent({
          title: newTitle,
          text: newContent
        });
        
        setShowRevisionForm(false);
        setRevisionInstructions('');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to revise content');
      }
    } catch (err) {
      setError('Error revising content');
      console.error('Revision error:', err);
    } finally {
      setIsRevising(false);
    }
  };

  const handleCopyContent = () => {
    if (generatedContent) {
      const fullContent = `# ${generatedContent.title}\n\n${generatedContent.text}`;
      navigator.clipboard.writeText(fullContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleApproveKeyword = async () => {
    if (!formData.mainKeyword) return;

    setIsApproving(true);
    try {
      const response = await fetch('/.netlify/functions/smart-writer-mark-keyword', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keyword: formData.mainKeyword,
          action: 'approve'
        })
      });

      if (response.ok) {
        // Clear the generated content and form
        setGeneratedContent(null);
        setFormData(prev => ({ ...prev, mainKeyword: '', title: '', secondaryKeywords: [] }));
        
        // Reload keywords to update the dropdown
        const keywordsResponse = await fetch('/.netlify/functions/smart-writer-fetch-keywords', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mode: 'list' })
        });
        
        if (keywordsResponse.ok) {
          const data = await keywordsResponse.json();
          setAvailableKeywords(data.keywords || []);
        }

        await loadUsedKeywords();
        setShowApproveDialog(false);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to approve keyword');
      }
    } catch (err) {
      setError('Error approving keyword');
      console.error('Approve error:', err);
    } finally {
      setIsApproving(false);
    }
  };

  const handleRestoreKeyword = async () => {
    if (!selectedRestoreKeyword) return;

    setIsRestoring(true);
    try {
      const response = await fetch('/.netlify/functions/smart-writer-mark-keyword', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keyword: selectedRestoreKeyword,
          action: 'restore'
        })
      });

      if (response.ok) {
        // Reload keywords to update the dropdown
        const keywordsResponse = await fetch('/.netlify/functions/smart-writer-fetch-keywords', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mode: 'list' })
        });
        
        if (keywordsResponse.ok) {
          const data = await keywordsResponse.json();
          setAvailableKeywords(data.keywords || []);
        }

        await loadUsedKeywords();
        setShowRestoreDialog(false);
        setSelectedRestoreKeyword('');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to restore keyword');
      }
    } catch (err) {
      setError('Error restoring keyword');
      console.error('Restore error:', err);
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex overflow-hidden">
      {/* Sidebar */}
      <aside className="bg-white dark:bg-gray-800 shadow-sm w-80 flex flex-col">
        {/* Header Section */}
        <div className="border-b flex justify-center items-center h-20 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
          <div className="flex flex-col items-center justify-center py-3">
            <PenTool className="h-8 w-8 text-violet-600 mb-1" />
            <h1 className="text-base font-bold text-gray-900 dark:text-white">Smart Writer</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">AI Content Generation</p>
          </div>
        </div>

        {/* Prompt Configuration */}
        <div className="flex-1 p-3 overflow-y-auto">
          <div className="space-y-3">
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Prompt Configuration</h3>
            </div>

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-3 py-2 rounded-md text-sm">
                {error}
              </div>
            )}

            {/* Type Selection */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Type
              </label>
              <select
                value={formData.textType}
                onChange={(e) => handleFormChange('textType', e.target.value)}
                disabled={isLoadingTypes}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm disabled:opacity-50"
              >
                <option value="">
                  {isLoadingTypes ? 'Loading types...' : 'Select content type...'}
                </option>
                {contentTypes.map((type, index) => (
                  <option key={index} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* Model Selection */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                AI Model
              </label>
              <select
                value={formData.model}
                onChange={(e) => handleFormChange('model', e.target.value)}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm"
              >
                {Object.entries(GROUPED_MODELS).map(([category, models]) => (
                  <optgroup key={category} label={MODEL_CATEGORIES[category as keyof typeof MODEL_CATEGORIES]}>
                    {models.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={() => checkModelHealth(formData.model)}
                  disabled={isCheckingModelHealth || !formData.model}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 disabled:opacity-50 flex items-center gap-1"
                >
                  <Activity className="h-3 w-3" />
                  {isCheckingModelHealth ? 'Checking...' : 'Test Model'}
                </button>
                <button
                  onClick={() => setShowModelInfo(!showModelInfo)}
                  className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 flex items-center gap-1"
                >
                  <Info className="h-3 w-3" />
                  Model Info
                </button>
              </div>
              
              {/* Model Information Panel */}
              {showModelInfo && modelStatus && (
                <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md text-xs">
                  <div className="font-medium text-gray-900 dark:text-white mb-2">
                    Available Models: {modelStatus.totalModels}
                  </div>
                  <div className="space-y-1 text-gray-600 dark:text-gray-400">
                    <div>• Fast models: {modelStatus.categories?.fast?.length || 0}</div>
                    <div>• Cost-effective: {modelStatus.categories?.costEffective?.length || 0}</div>
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                    <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">Fallback Chain:</div>
                    <div className="text-gray-500 dark:text-gray-400">
                      {modelStatus.fallbackChain?.join(' → ') || 'Not available'}
                    </div>
                  </div>
                  {modelStatus.tips && (
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                      <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">Tips:</div>
                      <div className="space-y-1 text-gray-500 dark:text-gray-400">
                        <div>• Speed: {modelStatus.tips.forSpeed}</div>
                        <div>• Cost: {modelStatus.tips.forCost}</div>
                        <div>• Quality: {modelStatus.tips.forQuality}</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Length Input */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Length
              </label>
              <input
                type="text"
                value={formData.length}
                onChange={(e) => handleFormChange('length', e.target.value)}
                placeholder="e.g., 1200 words"
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm"
              />
            </div>

            {/* Main Keyword Selection */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Main Keyword
              </label>
              <div className="relative">
                <div className="relative">
                  <input
                    type="text"
                    value={keywordSearch}
                    onChange={handleKeywordSearch}
                    onFocus={() => setShowKeywordDropdown(true)}
                    onBlur={handleKeywordInputBlur}
                    placeholder={isLoadingKeywords ? 'Loading keywords...' : 'Search or select a keyword...'}
                    disabled={isLoadingKeywords}
                    className="w-full p-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm disabled:opacity-50"
                    required
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
                
                {showKeywordDropdown && filteredKeywords.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {filteredKeywords.map((keyword, index) => (
                      <div
                        key={index}
                        onClick={() => handleKeywordSelect(keyword)}
                        className="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600 last:border-b-0"
                      >
                        {keyword}
                      </div>
                    ))}
                  </div>
                )}
                
                {showKeywordDropdown && filteredKeywords.length === 0 && keywordSearch.trim() && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg">
                    <div className="px-3 py-2 text-gray-500 dark:text-gray-400">
                      No keywords found matching "{keywordSearch}"
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Secondary Keywords - Auto-filled */}
            {formData.mainKeyword && formData.secondaryKeywords.length > 0 && (
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Secondary Keywords
                </label>
                <input
                  type="text"
                  value={formData.secondaryKeywords.join(', ')}
                  readOnly
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-600 text-gray-900 dark:text-white text-sm"
                />
              </div>
            )}

            {/* Title - Auto-filled */}
            {formData.mainKeyword && formData.title && (
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  readOnly
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-600 text-gray-900 dark:text-white text-sm"
                />
              </div>
            )}

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              disabled={isGenerating || !formData.textType || !formData.mainKeyword}
              className="w-full bg-violet-600 hover:bg-violet-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center text-sm disabled:cursor-not-allowed"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <PenTool className="h-4 w-4 mr-2" />
                  Generate Content
                </>
              )}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <div className="border-t dark:border-gray-700 px-4 py-2">
          <div
            onClick={() => navigate('/dashboard')}
            className="flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer rounded-md"
          >
            <Home className="mr-3 h-4 w-4" />
            Apps Hub
          </div>
        </div>

        {/* User Info Section */}
        <div className="border-t dark:border-gray-700 p-4 mt-auto bg-white dark:bg-gray-800">
          <div className="flex flex-col gap-4">
            {displayName && (
              <div className="text-center">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {displayName}
                </span>
              </div>
            )}
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => {
                  logout();
                  navigate('/');
                }}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Logout"
              >
                <LogOut className="h-4 w-4" />
              </button>
              <button
                onClick={toggleDarkMode}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                title={isDarkMode ? "Light Mode" : "Dark Mode"}
              >
                {isDarkMode ? (
                  <Sun className="h-4 w-4" />
                ) : (
                  <Moon className="h-4 w-4" />
                )}
              </button>
              <NotificationMenu>
                <UserAvatar size="sm" className="h-8 w-8" />
              </NotificationMenu>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Smart Writer</h1>
              <p className="text-gray-600 dark:text-gray-400">Configure your prompt in the side panel and generate AI-powered content.</p>
            </div>

            {/* Generated Content Display */}
            {generatedContent ? (
              <div className="space-y-6">
                {/* Generated Title */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <div className="flex items-center mb-4">
                    <FileText className="h-5 w-5 text-violet-600 mr-2" />
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Generated Title</h2>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    {generatedContent.title}
                  </h3>
                </div>

                {/* Generated Text */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <PenTool className="h-5 w-5 text-violet-600 mr-2" />
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Generated Content</h2>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setShowRevisionForm(!showRevisionForm)}
                        className="flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                      >
                        <PenTool className="h-4 w-4 mr-1" />
                        {showRevisionForm ? 'Hide Revision' : 'Revise'}
                      </button>
                      <button
                        onClick={handleCopyContent}
                        className="flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                      >
                        {copied ? (
                          <>
                            <Check className="h-4 w-4 mr-1" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4 mr-1" />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Revision Form */}
                  {showRevisionForm && (
                    <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-md">
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Revision Instructions
                      </h3>
                      <textarea
                        value={revisionInstructions}
                        onChange={(e) => setRevisionInstructions(e.target.value)}
                        placeholder="Describe the changes you want to make to the content..."
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm min-h-[100px]"
                      />
                      <div className="mt-2 flex justify-end">
                        <button
                          onClick={handleRevise}
                          disabled={isRevising || !revisionInstructions.trim()}
                          className="bg-violet-600 hover:bg-violet-700 disabled:bg-gray-400 text-white font-medium py-1 px-3 rounded-md transition-colors duration-200 flex items-center justify-center text-sm disabled:cursor-not-allowed"
                        >
                          {isRevising ? (
                            <>
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              Revising...
                            </>
                          ) : (
                            'Apply Revision'
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="prose prose-violet dark:prose-invert max-w-none">
                    <div className="markdown-content text-gray-900 dark:text-white">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {generatedContent.text}
                      </ReactMarkdown>
                    </div>
                  </div>
                  <style>
                    {`
                    .markdown-content h1 {
                      font-size: 1.8rem;
                      font-weight: 700;
                      margin-top: 1.5rem;
                      margin-bottom: 1rem;
                      color: #4c1d95;
                    }
                    .markdown-content h2 {
                      font-size: 1.5rem;
                      font-weight: 600;
                      margin-top: 1.5rem;
                      margin-bottom: 0.75rem;
                      color: #6d28d9;
                    }
                    .markdown-content h3 {
                      font-size: 1.25rem;
                      font-weight: 600;
                      margin-top: 1.25rem;
                      margin-bottom: 0.75rem;
                      color: #7c3aed;
                    }
                    .markdown-content p {
                      margin-bottom: 1rem;
                      line-height: 1.7;
                    }
                    .markdown-content ul, .markdown-content ol {
                      margin-left: 1.5rem;
                      margin-bottom: 1rem;
                    }
                    .markdown-content li {
                      margin-bottom: 0.5rem;
                    }
                    .markdown-content blockquote {
                      border-left: 4px solid #8b5cf6;
                      padding-left: 1rem;
                      font-style: italic;
                      margin: 1rem 0;
                    }
                    .markdown-content code {
                      background-color: #f3f4f6;
                      padding: 0.2rem 0.4rem;
                      border-radius: 0.25rem;
                      font-family: monospace;
                    }
                    .markdown-content pre {
                      background-color: #1e293b;
                      padding: 1rem;
                      border-radius: 0.5rem;
                      overflow-x: auto;
                      margin: 1rem 0;
                    }
                    .markdown-content pre code {
                      background-color: transparent;
                      padding: 0;
                      color: #e2e8f0;
                    }
                    .markdown-content a {
                      color: #8b5cf6;
                      text-decoration: underline;
                    }
                    .markdown-content table {
                      width: 100%;
                      border-collapse: collapse;
                      margin: 1rem 0;
                    }
                    .markdown-content th, .markdown-content td {
                      border: 1px solid #e5e7eb;
                      padding: 0.5rem;
                    }
                    .markdown-content th {
                      background-color: #f9fafb;
                    }
                    .dark .markdown-content h1 {
                      color: #c4b5fd;
                    }
                    .dark .markdown-content h2 {
                      color: #ddd6fe;
                    }
                    .dark .markdown-content h3 {
                      color: #ede9fe;
                    }
                    .dark .markdown-content code {
                      background-color: #1e293b;
                    }
                    .dark .markdown-content pre {
                      background-color: #0f172a;
                    }
                    .dark .markdown-content a {
                      color: #a78bfa;
                    }
                    .dark .markdown-content th, .dark .markdown-content td {
                      border-color: #334155;
                    }
                    .dark .markdown-content th {
                      background-color: #1e293b;
                    }
                    `}
                  </style>
                </div>

                {/* Approve and Restore Buttons */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mt-6">
                  <div className="flex items-center justify-between gap-4">
                    {/* Approve Button */}
                    <button
                      onClick={() => setShowApproveDialog(true)}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-6 rounded-md transition-colors duration-200 flex items-center justify-center"
                    >
                      <CheckCircle className="h-5 w-5 mr-2" />
                      Approve & Mark as Used
                    </button>

                    {/* Restore Button - Always Visible */}
                    <button
                      onClick={() => setShowRestoreDialog(true)}
                      disabled={usedKeywords.length === 0}
                      className="flex-1 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white font-medium py-3 px-6 rounded-md transition-colors duration-200 flex items-center justify-center disabled:cursor-not-allowed"
                      title={usedKeywords.length === 0 ? 'No used keywords to restore' : 'Restore a previously used keyword'}
                    >
                      <RotateCcw className="h-5 w-5 mr-2" />
                      Restore Keyword {usedKeywords.length > 0 && `(${usedKeywords.length})`}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <PenTool className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Smart Writer - In Progress</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  This feature is currently being developed. Content generation functionality will be available soon.
                </p>
                <div className="max-w-md mx-auto space-y-2 text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center justify-center">
                    <span className="w-6 h-6 bg-violet-100 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 rounded-full flex items-center justify-center text-xs font-medium mr-3">1</span>
                    Choose a content type from the dropdown
                  </div>
                  <div className="flex items-center justify-center">
                    <span className="w-6 h-6 bg-violet-100 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 rounded-full flex items-center justify-center text-xs font-medium mr-3">2</span>
                    Set your target length
                  </div>
                  <div className="flex items-center justify-center">
                    <span className="w-6 h-6 bg-violet-100 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 rounded-full flex items-center justify-center text-xs font-medium mr-3">3</span>
                    Pick a main keyword to auto-fill details
                  </div>
                  <div className="flex items-center justify-center">
                    <span className="w-6 h-6 bg-violet-100 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 rounded-full flex items-center justify-center text-xs font-medium mr-3">4</span>
                    Click Generate to create content
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Approve Confirmation Dialog */}
      {showApproveDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <AlertCircle className="h-6 w-6 text-orange-500 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Confirm Keyword Approval</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Are you sure you want to approve this content and mark the keyword <strong>"{formData.mainKeyword}"</strong> as used?
            </p>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3 mb-4">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                This will:
              </p>
              <ul className="text-sm text-blue-700 dark:text-blue-400 mt-2 space-y-1 list-disc list-inside">
                <li>Remove "{formData.mainKeyword}" from the available keywords list</li>
                <li>Clear the current generated content</li>
                <li>Reset the form for the next generation</li>
                <li>Update the keyword status for all users</li>
              </ul>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowApproveDialog(false)}
                disabled={isApproving}
                className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 py-2 px-4 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleApproveKeyword}
                disabled={isApproving}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md disabled:opacity-50 flex items-center justify-center"
              >
                {isApproving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Approving...
                  </>
                ) : (
                  'Approve'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Keyword Dialog */}
      {showRestoreDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <RotateCcw className="h-6 w-6 text-orange-500 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Restore Used Keyword</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Select a keyword to restore to the available list:
            </p>
            <select
              value={selectedRestoreKeyword}
              onChange={(e) => setSelectedRestoreKeyword(e.target.value)}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white mb-4"
            >
              <option value="">Choose a keyword to restore...</option>
              {usedKeywords.map((keyword, index) => (
                <option key={index} value={keyword}>{keyword}</option>
              ))}
            </select>
            {selectedRestoreKeyword && (
              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md p-3 mb-4">
                <p className="text-sm text-orange-800 dark:text-orange-300">
                  This will:
                </p>
                <ul className="text-sm text-orange-700 dark:text-orange-400 mt-2 space-y-1 list-disc list-inside">
                  <li>Add "{selectedRestoreKeyword}" back to the available keywords</li>
                  <li>Make it selectable in the keyword dropdown again</li>
                  <li>Allow content generation for this keyword</li>
                </ul>
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRestoreDialog(false);
                  setSelectedRestoreKeyword('');
                }}
                disabled={isRestoring}
                className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 py-2 px-4 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRestoreKeyword}
                disabled={isRestoring || !selectedRestoreKeyword}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-2 px-4 rounded-md disabled:opacity-50 flex items-center justify-center"
              >
                {isRestoring ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Restoring...
                  </>
                ) : (
                  'Restore'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartWriterDashboard;